import React, { createContext, useContext, useMemo } from 'react';
import { FCM_WEB_PUSH_ENABLED } from '../config/fcmWebPushEnabled';
import { useAuth } from './AuthContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useNotificationAlertsSetup } from '../hooks/useNotificationAlertsSetup';
import { useFcmPushMessageListener } from '../hooks/useFcmPushMessageListener';
import { usePortalNotificationActions } from '../hooks/usePortalNotificationActions';
import NotificationPermissionBanner from '../components/NotificationPermissionBanner';
import NotificationAlertsConsentDialog from '../components/NotificationAlertsConsentDialog';

const NotificationContext = createContext(null);

/**
 * Web push state + foreground messages. Setup UI: bottom banner, header bell menu, consent dialog.
 */
export const NotificationProvider = ({ children }) => {
  const { isAuthenticated, userRole, loading, firebaseUid, incrementNotificationCount } = useAuth();
  const sessionReady = isAuthenticated && !loading;
  const push = usePushNotifications({
    enabled: FCM_WEB_PUSH_ENABLED,
    sessionReady,
    authUid: firebaseUid,
  });

  const alertsSetup = useNotificationAlertsSetup(push, {
    enabled: FCM_WEB_PUSH_ENABLED,
    isAuthenticated,
    loading,
  });

  const notificationActions = usePortalNotificationActions();

  const canHandlePushClicks = FCM_WEB_PUSH_ENABLED && sessionReady && Boolean(userRole);
  const canListenForForegroundMessages = canHandlePushClicks && push.isConnected;

  useFcmPushMessageListener({
    clicksEnabled: canHandlePushClicks,
    messagesEnabled: canListenForForegroundMessages,
    openNotification: notificationActions.openNotification,
    onNewNotification: incrementNotificationCount,
  });

  const contextValue = useMemo(
    () => ({
      ...push,
      alertsSetup,
      ...notificationActions,
    }),
    [alertsSetup, notificationActions, push],
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {FCM_WEB_PUSH_ENABLED && sessionReady ? (
        <>
          <NotificationAlertsConsentDialog {...alertsSetup.consentDialogProps} />
          <NotificationPermissionBanner />
        </>
      ) : null}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => useContext(NotificationContext);

export default NotificationProvider;
